import React, { createContext, useContext, useCallback, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBottomSheet } from '../../hooks/useBottomSheet';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useIdentity } from '../../hooks/useIdentity';
import { IconButton } from '../ui';
import { FloatingActionButton } from '../ui/FloatingActionButton';
import type { FABSlot } from '../ui/FloatingActionButton';
import { QuickCreateTask } from './QuickCreateTask';
import { QuickPraise } from './QuickPraise';
import { QuickCheckin } from './QuickCheckin';
import { FAB_ACTION_REGISTRY, DOM_DEFAULTS, SUB_DEFAULTS, SWITCH_DEFAULTS, safetyRouteActionKeys } from './fabActionRegistry';
import { useFABPreferences } from '../../hooks/queries/useFABPreferences';
import { useFABContext } from '../../hooks/useFABContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { isQuickActionsCollapsedRoute } from './quickActionsRoutes';
import { CRISIS_RESOURCES_ID } from '../safety/crisisResourcesAnchor';

// ─── Context ─────────────────────────────────────────────────────────────────

interface QuickActionsContextType {
  openQuickTask: () => void;
  openQuickPraise: () => void;
  openQuickCheckin: () => void;
  suppressFAB: () => void;
  unsuppressFAB: () => void;
}

const QuickActionsContext = createContext<QuickActionsContextType | undefined>(undefined);

// Non-throwing variant for consumers that may render outside the provider
// (e.g. component tests) and need to degrade gracefully instead of crashing.
export function useQuickActionsOptional(): QuickActionsContextType | undefined {
  return useContext(QuickActionsContext);
}

export function useQuickActions(): QuickActionsContextType {
  const ctx = useQuickActionsOptional();
  if (!ctx) {
    throw new Error('useQuickActions must be used within QuickActionsProvider');
  }
  return ctx;
}

// First-use callout sits beside the FAB tray, so it must clear the same mobile
// bottom-nav offset the tray does (FloatingActionButton bottomPos).
export function onboardingCalloutBottom(layout: 'vertical' | 'narrow', fabBottomOffset: number): string {
  const base = layout === 'vertical' ? 16 : 80;
  return `calc(${base + fabBottomOffset}px + env(safe-area-inset-bottom, 0px))`;
}

// ─── Provider ────────────────────────────────────────────────────────────────

interface QuickActionsProviderProps {
  children: React.ReactNode;
}

export function QuickActionsProvider({ children }: QuickActionsProviderProps) {
  const { features } = useFeatures();
  const { identity } = useIdentity();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const taskSheet = useBottomSheet();
  const praiseSheet = useBottomSheet();
  const checkinSheet = useBottomSheet();

  const { preferences, saveFABPreferences } = useFABPreferences();
  const contextOverride = useFABContext();

  // Onboarding callout dismiss state (optimistic)
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);

  // Ref-counted suppression — each suppressor increments on mount, decrements on unmount.
  // FAB stashes when count > 0. Counter handles concurrent suppressors during route transitions.
  const [fabSuppressionCount, setFabSuppressionCount] = useState(0);
  const suppressFAB = useCallback(() => setFabSuppressionCount(c => c + 1), []);
  const unsuppressFAB = useCallback(() => setFabSuppressionCount(c => Math.max(0, c - 1)), []);

  // All hooks must be called before any early return (React rules of hooks)
  const openQuickTask = useCallback(() => taskSheet.open(), [taskSheet]);
  const openQuickPraise = useCallback(() => praiseSheet.open(), [praiseSheet]);
  const openQuickCheckin = useCallback(() => checkinSheet.open(), [checkinSheet]);

  // Navigation callbacks
  const goToTaskCreate = useCallback(() => navigate('/tasks/create'), [navigate]);
  const goToJournal = useCallback(() => navigate('/chronicle/journal?action=new'), [navigate]);
  const goToReviews = useCallback(() => navigate('/command/reviews'), [navigate]);
  const goToMessages = useCallback(() => navigate('/messages'), [navigate]);
  const goToTasks = useCallback(() => navigate('/tasks'), [navigate]);
  const goToCrisisLines = useCallback(() => navigate(`/safeword#${CRISIS_RESOURCES_ID}`), [navigate]);
  const goToSafetyHub = useCallback(() => navigate('/safety'), [navigate]);
  const goToSafetyTimer = useCallback(() => navigate('/safety/check-in'), [navigate]);
  const goToHealthCheck = useCallback(() => navigate('/command/chastity'), [navigate]);
  const goToOverdue = useCallback(() => navigate('/tasks?filter=overdue'), [navigate]);
  const goToExtensionReviews = useCallback(
    () => navigate('/command/reviews?filter=extensions'),
    [navigate]
  );

  // Map action keys to onClick callbacks
  const actionCallbackMap = useMemo<Record<string, () => void>>(
    () => ({
      assign_task: goToTaskCreate,
      send_praise: openQuickPraise,
      check_on_sub: goToReviews,
      quick_message: goToMessages,
      view_tasks: goToTasks,
      journal_entry: goToJournal,
      check_in: openQuickCheckin,
      message_dom: goToMessages,
      safety_timer: goToSafetyTimer,
      safety_hub: goToSafetyHub,
      crisis_lines: goToCrisisLines,
      health_check: goToHealthCheck,
      view_overdue: goToOverdue,
      review_extension: goToExtensionReviews,
    }),
    [
      goToTaskCreate,
      openQuickPraise,
      goToReviews,
      goToMessages,
      goToTasks,
      goToJournal,
      openQuickCheckin,
      goToSafetyTimer,
      goToSafetyHub,
      goToCrisisLines,
      goToHealthCheck,
      goToOverdue,
      goToExtensionReviews,
    ]
  );

  // Resolve role-default action keys
  const roleDefaults = useMemo((): [string, string, string] => {
    if (identity === 'dominant') return DOM_DEFAULTS as unknown as [string, string, string];
    if (identity === 'switch') return SWITCH_DEFAULTS as unknown as [string, string, string];
    return SUB_DEFAULTS as unknown as [string, string, string];
  }, [identity]);

  // Pillar-aware slot 0 key — adapts to current route when no saved preferences
  const pillarSlot0Key = useMemo((): string => {
    if (identity === 'dominant') {
      if (pathname.startsWith('/chronicle')) return 'journal_entry';
      if (pathname.startsWith('/connect')) return 'quick_message';
      if (pathname.startsWith('/command')) return 'assign_task';
    } else {
      if (pathname.startsWith('/chronicle')) return 'journal_entry';
      if (pathname.startsWith('/connect')) return 'quick_message';
      if (pathname.startsWith('/command')) return 'view_tasks';
    }
    return roleDefaults[0];
  }, [identity, pathname, roleDefaults]);

  // Resolve configured keys from preferences, falling back to pillar-aware defaults
  const configuredKeys = useMemo((): [string, string, string] => {
    const saved = preferences?.actionKeys;
    if (saved && saved.length >= 3) {
      return [saved[0], saved[1], saved[2]] as [string, string, string];
    }
    return [pillarSlot0Key, roleDefaults[1], roleDefaults[2]];
  }, [preferences?.actionKeys, roleDefaults, pillarSlot0Key]);

  // Build a FABSlot from an action key
  const buildSlot = useCallback(
    (key: string): FABSlot => {
      const def = FAB_ACTION_REGISTRY[key];
      return {
        key,
        label: def?.label ?? key,
        icon: def?.icon ?? null,
        onClick: actionCallbackMap[key] ?? (() => {}),
      };
    },
    [actionCallbackMap]
  );

  // Resolve the 3 slots, substituting slot 0 with context override when active
  const collapseRail = isQuickActionsCollapsedRoute(pathname);
  const resolvedSlots = useMemo((): [FABSlot, FABSlot, FABSlot] => {
    // Safety routes: fixed safety actions for every role; saved preferences are neither read nor changed.
    if (collapseRail) {
      const [a, b, c] = safetyRouteActionKeys(identity, features.emergencyTimer);
      return [buildSlot(a), buildSlot(b), buildSlot(c)];
    }
    const slot1 = buildSlot(configuredKeys[1]);
    const slot2 = buildSlot(configuredKeys[2]);

    if (contextOverride) {
      const overrideSlot: FABSlot = {
        key: contextOverride.actionKey,
        label: contextOverride.label,
        icon: contextOverride.icon,
        onClick: actionCallbackMap[contextOverride.actionKey] ?? (() => {}),
        tinted: contextOverride.tinted,
        badge: contextOverride.badge,
        contextReason: contextOverride.reason,
      };
      return [overrideSlot, slot1, slot2];
    }

    return [buildSlot(configuredKeys[0]), slot1, slot2];
  }, [collapseRail, identity, features.emergencyTimer, contextOverride, configuredKeys, buildSlot, actionCallbackMap]);

  const layout = preferences?.layout ?? 'vertical';
  const isMobile = useIsMobile();
  const fabBottomOffset = isMobile ? 60 : 0;
  // FloatingActionButton renders nothing on desktop, so neither does its callout
  const showOnboarding =
    isMobile &&
    !collapseRail &&
    preferences !== null &&
    preferences?.onboarded === false &&
    !onboardingDismissed;

  const handleDismissOnboarding = useCallback(() => {
    setOnboardingDismissed(true);
    saveFABPreferences({ onboarded: true });
  }, [saveFABPreferences]);

  // Feature flag: mobileBottomSheets — when off, render children only (no FAB, no sheets)
  if (!features.mobileBottomSheets) {
    return <>{children}</>;
  }

  return (
    <QuickActionsContext.Provider value={{ openQuickTask, openQuickPraise, openQuickCheckin, suppressFAB, unsuppressFAB }}>
      {children}

      {/* Quick action sheets — mounted once, toggled via context */}
      <QuickCreateTask isOpen={taskSheet.isOpen} onClose={taskSheet.close} />
      <QuickPraise isOpen={praiseSheet.isOpen} onClose={praiseSheet.close} />
      <QuickCheckin isOpen={checkinSheet.isOpen} onClose={checkinSheet.close} />

      {/* First-use onboarding callout.
          role=status + aria-live must NOT intercept pointer events — it overlays
          the bottom-right region where CTAs (Create Task, form submits) live on
          mobile. Wrapper is pointer-events:none; only the explicit Dismiss
          button receives clicks. */}
      {showOnboarding && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            // vertical: gap left of the ~70px-wide tray for the pointer; narrow: above the tray
            right: layout === 'vertical' ? 96 : 16,
            bottom: onboardingCalloutBottom(layout, fabBottomOffset),
            zIndex: 41,
            background: 'rgba(26,34,51,0.96)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12,
            padding: '10px 14px',
            paddingRight: 32,
            color: 'var(--theme-fab-icon)',
            fontSize: 13,
            fontWeight: 500,
            maxWidth: 240,
            boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
            pointerEvents: 'none',
          }}
        >
          Shortcuts: {resolvedSlots.map(s => s.label).join(' · ')}. The {layout === 'vertical' ? 'top' : 'first'} one
          changes with the page you're on. Swipe right to hide them, left to bring them back.
          {/* Pointer toward the tray: right edge (vertical) or bottom edge (narrow) */}
          <span
            aria-hidden="true"
            data-testid="fab-onboarding-pointer"
            style={{
              position: 'absolute',
              width: 10,
              height: 10,
              background: 'rgba(26,34,51,0.96)',
              transform: 'rotate(45deg)',
              ...(layout === 'vertical'
                ? {
                    right: -6,
                    bottom: 24,
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                    borderRight: '1px solid rgba(255,255,255,0.08)',
                  }
                : {
                    bottom: -6,
                    right: 70,
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    borderRight: '1px solid rgba(255,255,255,0.08)',
                  }),
            }}
          />
          <IconButton
            icon={<span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>×</span>}
            aria-label="Dismiss onboarding"
            type="button"
            onClick={handleDismissOnboarding}
            variant="ghost"
            size="sm"
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 24,
              height: 24,
              color: 'rgba(230,234,242,0.7)',
              pointerEvents: 'auto',
              borderRadius: 4,
            }}
          />
        </div>
      )}

      {/* 3-slot FAB tray — slides off-screen when suppressed (e.g. MessageThread) */}
      {/* On safety routes (see quickActionsRoutes.ts) the rail starts collapsed to its edge tab so it never
          covers crisis lines; the user's saved stash state is untouched and returns on leaving the route. */}
      <FloatingActionButton
        slots={resolvedSlots}
        layout={layout}
        bottomOffset={fabBottomOffset}
        suppressed={fabSuppressionCount > 0}
        collapsedOnRoute={collapseRail}
        routeKey={pathname}
      />
    </QuickActionsContext.Provider>
  );
}
